// Orquestrador da aba "Inscritos" da tela de manutenção de simulado: uma
// leitura (design.md Decisão 3), a fase do simulado derivada uma única vez
// (Decisão 6) e repassada para Resumo/Tabela/diálogo, e busca/filtro/
// ordenação em memória sobre a lista carregada (Decisão 4). Resumo, Toolbar,
// Tabela e o diálogo de cancelamento são lidos como contrato fixo — todo o
// vocabulário (fase, rótulos, pipeline, motivos) mora em `inscritosVocabulary.ts`
// e nunca é reimplementado aqui.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { Loader2, RotateCcw } from 'lucide-react';

import type { MocktestRegistrationsResponse, RegistrationCancelPayload } from '../../../sdk/@types/Simulado';
import InscritoService from '../../../sdk/services/InscritoService';
import { readApiError, type ApiError } from '../../../sdk/errors/apiError';
import AccessDenied from '../AccessDenied/AccessDenied';

import { InscritosResumo } from './InscritosResumo';
import { InscritosToolbar } from './InscritosToolbar';
import type { InscritosStatusFilterValue } from './InscritosToolbar';
import { InscritosTable } from './InscritosTable';
import CancelarInscricaoModal from './CancelarInscricaoModal';
import { fase, filterInscritos, sortInscritos, toCsv } from './inscritosVocabulary';
import type { Fase, InscritoSortKey, RegistrationEntry } from './inscritosVocabulary';

export interface InscritosPanelService {
  getMocktestRegistrations: (mocktestId: string) => Promise<MocktestRegistrationsResponse>;
  cancelRegistration: (
    registrationId: string,
    payload: RegistrationCancelPayload,
  ) => Promise<RegistrationEntry>;
}

export interface InscritosPanelProps {
  mocktestId: string;
  mocktestName: string;
  /** Ponto de substituição para Storybook/testes — omitir na app real; cai em `InscritoService`. */
  service?: InscritosPanelService;
}

// `InscritoService` é uma classe com métodos `static` que leem `this.Http` /
// `this.getData` (herdados de `Service`, também `static`). Guardar o método
// "nu" num objeto literal perderia esse `this`: chamado depois como
// `svc.getMocktestRegistrations(...)`, o `this` dentro do método seria `svc`
// — um objeto sem `Http` — e quebraria em runtime. Por isso cada entrada é
// uma arrow function que invoca `InscritoService.algo(...)` explicitamente.
const DEFAULT_SERVICE: InscritosPanelService = {
  getMocktestRegistrations: (mocktestId) => InscritoService.getMocktestRegistrations(mocktestId),
  cancelRegistration: (registrationId, payload) =>
    InscritoService.cancelRegistration(registrationId, payload),
};

// Cache em nível de módulo, chaveada por `mocktestId` — mesma forma do cache
// de `loggedUser.ts` ("chaveado pelo token, não por um booleano"). O painel só
// monta enquanto a aba "Inscritos" está aberta e desmonta ao trocar de aba;
// sem isto, reabrir a aba refaria a leitura toda vez, mesmo sem nada ter
// mudado no servidor.
const registrationsCache = new Map<string, Promise<MocktestRegistrationsResponse>>();

function loadRegistrations(
  mocktestId: string,
  getter: InscritosPanelService['getMocktestRegistrations'],
  bypassCache: boolean,
): Promise<MocktestRegistrationsResponse> {
  if (bypassCache) return getter(mocktestId);
  let pending = registrationsCache.get(mocktestId);
  if (!pending) {
    pending = getter(mocktestId);
    registrationsCache.set(mocktestId, pending);
    pending.catch(() => registrationsCache.delete(mocktestId)); // permite tentar de novo após falha
  }
  return pending;
}

function downloadCsv(csv: string, mocktestId: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `inscritos-${mocktestId}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

type LoadOutcome =
  | { status: 'loading' }
  | { status: 'loaded' }
  | { status: 'denied'; error: ApiError }
  | { status: 'failed'; error: ApiError };

export function InscritosPanel({ mocktestId, mocktestName, service }: InscritosPanelProps) {
  const navigate = useNavigate();
  const svc = service ?? DEFAULT_SERVICE;

  const [data, setData] = useState<MocktestRegistrationsResponse | null>(null);
  const [outcome, setOutcome] = useState<LoadOutcome>({ status: 'loading' });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<InscritosStatusFilterValue>('todos');
  const [sort, setSort] = useState<InscritoSortKey>('nome');
  const [cancelTarget, setCancelTarget] = useState<RegistrationEntry | null>(null);

  // `svc !== DEFAULT_SERVICE` é a mesma condição de "um `service` próprio foi
  // passado" (Storybook/testes) — escrita assim, contra a constante, em vez
  // de uma segunda variável `Boolean(service)`, porque `svc` já é a
  // dependência que este `useCallback` precisa ter; uma segunda variável
  // derivada dela teria de entrar nas deps por conta própria (o ESLint não
  // sabe que uma deriva da outra), o que o exhaustive-deps acusaria sem um
  // eslint-disable — e este arquivo não deve acrescentar nenhum.
  const fetchRegistrations = useCallback(
    (forceFresh: boolean) =>
      loadRegistrations(mocktestId, svc.getMocktestRegistrations, svc !== DEFAULT_SERVICE || forceFresh),
    [mocktestId, svc],
  );

  const retryLoad = useCallback(() => {
    setOutcome({ status: 'loading' });
    fetchRegistrations(false)
      .then((response) => {
        setData(response);
        setOutcome({ status: 'loaded' });
      })
      .catch((error) => {
        const apiError = readApiError(error);
        setOutcome(apiError.kind ? { status: 'denied', error: apiError } : { status: 'failed', error: apiError });
      });
  }, [fetchRegistrations]);

  // Uma leitura ao abrir a aba a primeira vez, nenhuma a mais ao reabrir — é
  // o cache acima, não este efeito, que garante isso; o efeito em si roda a
  // cada montagem, como qualquer efeito de busca.
  useEffect(() => {
    retryLoad();
  }, [retryLoad]);

  // Depois de um cancelamento (sucesso ou janela expirada), os agregados e o
  // status da linha têm de vir do servidor de novo — nunca recalculados sobre
  // as linhas que já estavam na tela (design.md Decisão 3). Por isso sempre
  // ignora o cache, mesmo quando `svc` é o `DEFAULT_SERVICE`.
  const reloadAfterCancel = useCallback(() => {
    return fetchRegistrations(true)
      .then((response) => {
        setData(response);
        setOutcome({ status: 'loaded' });
      })
      .catch(() => {
        // A ação de cancelamento já foi decidida (sucesso ou 409); uma falha
        // só nesta releitura não desfaz isso, então não troca `outcome` para
        // 'failed' — isso apagaria o Resumo/Tabela bons que já estão na tela.
        notification.error({ message: 'Não foi possível atualizar a lista de inscritos.' });
      });
  }, [fetchRegistrations]);

  // O relógio é lido em um só lugar deste arquivo — aqui, junto da derivação
  // de fase (design.md de `simulado-inscritos-tab`, Decisão 6; design.md de
  // `simulado-cancelamento-efetivo`, cenário "O início do simulado tem uma só
  // origem") — e repassado tanto para `fase()` quanto para `<InscritosResumo>`
  // (prop `now`), para que a fase e o "Começa em" nunca se baseiem em
  // instantes diferentes. Um único `useMemo`, não dois adjacentes: separar
  // `now` num `useMemo` próprio cujo corpo não lê `data` acusaria
  // `react-hooks/exhaustive-deps` ("unnecessary dependency: 'data'"), e este
  // arquivo não deve acrescentar nenhum eslint-disable (comentário de
  // `fetchRegistrations`, acima). Calculado uma vez por carregamento de
  // `data`, nunca a cada busca/filtro/ordenação.
  const { now, faseAtual } = useMemo<{ now: Date; faseAtual: Fase | null }>(() => {
    const instant = new Date();
    return {
      now: instant,
      faseAtual: data ? fase(data.mocktestStart, data.mocktestEnd, instant) : null,
    };
  }, [data]);

  // "Inscrito" e "Não iniciou" são o mesmo `derivedStatus: 'nao_iniciou'`, só
  // lido diferente por fase (InscritosToolbar.tsx) — a ponte entre o valor do
  // filtro e o vocabulário mora aqui, nunca dentro do pipeline puro.
  const filteredSorted = useMemo<RegistrationEntry[]>(() => {
    if (!data) return [];
    const vocabStatus = status === 'inscrito' ? 'nao_iniciou' : status;
    const filtered = filterInscritos(data.registrations, { search, status: vocabStatus });
    return sortInscritos(filtered, sort);
  }, [data, search, status, sort]);

  const handleExportCsv = useCallback(() => {
    if (!faseAtual) return;
    downloadCsv(toCsv(filteredSorted, faseAtual), mocktestId);
  }, [filteredSorted, faseAtual, mocktestId]);

  const handleConfirmCancel = useCallback(
    (payload: RegistrationCancelPayload) => {
      if (!cancelTarget) return;
      svc
        .cancelRegistration(cancelTarget.registrationId, payload)
        .then(() => {
          setCancelTarget(null);
          notification.success({ message: 'Inscrição cancelada.' });
          return reloadAfterCancel();
        })
        .catch((error) => {
          const httpStatus = (error as { response?: { status?: number } })?.response?.status;
          if (httpStatus === 409) {
            // Janela expirada: o simulado começou enquanto o diálogo estava
            // aberto. É informação, não falha do administrador (spec.md) —
            // por isso `notification.info`, não `.error`.
            setCancelTarget(null);
            notification.info({
              message: 'O cancelamento já não é mais possível.',
              description: 'O simulado começou enquanto o diálogo estava aberto. A lista foi atualizada.',
            });
            reloadAfterCancel();
            return;
          }
          // Falha inesperada: o diálogo permanece aberto com o que foi
          // preenchido — não toca em `cancelTarget`.
          notification.error({ message: 'Não foi possível cancelar a inscrição. Tente novamente.' });
        });
    },
    [cancelTarget, svc, reloadAfterCancel],
  );

  if (outcome.status === 'loading') {
    return (
      <div
        role="status"
        aria-label="Carregando inscritos"
        className="flex items-center justify-center gap-2 rounded-card border border-edge bg-surface py-16"
      >
        <Loader2 size={18} className="animate-spin text-ink-muted" />
        <span className="text-sm text-ink-muted">Carregando inscritos…</span>
      </div>
    );
  }

  if (outcome.status === 'denied') {
    return (
      <AccessDenied kind={outcome.error.kind!} detail={outcome.error.raw} variant="panel" onNavigate={navigate} />
    );
  }

  if (outcome.status === 'failed') {
    return (
      <div className="rounded-card border border-edge bg-surface p-4 text-sm">
        <p className="text-ink">Não foi possível carregar os inscritos.</p>
        {outcome.error.raw && <p className="mt-1 text-xs text-ink-muted">{outcome.error.raw}</p>}
        <button
          type="button"
          onClick={retryLoad}
          className="mt-3 inline-flex h-9 items-center gap-2 rounded-input border border-edge bg-surface px-3 font-medium text-ink-secondary hover:border-edge-strong hover:bg-surface-hover hover:text-ink"
        >
          <RotateCcw size={15} />
          Tentar de novo
        </button>
      </div>
    );
  }

  // Inalcançável na prática: `data`/`faseAtual` só ficam nulos enquanto
  // `outcome.status` é 'loading', já tratado acima — `setData` e
  // `setOutcome({status: 'loaded'})` sempre andam juntos. Guarda só para o
  // TypeScript: são estados próprios, não um campo de `outcome`.
  if (!data || !faseAtual) return null;

  return (
    <div className="flex flex-col gap-4">
      <InscritosResumo
        summary={data.summary}
        fase={faseAtual}
        mocktestStart={data.mocktestStart}
        registrations={data.registrations}
        now={now}
      />
      <InscritosToolbar
        fase={faseAtual}
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        sort={sort}
        onSortChange={setSort}
        onExportCsv={handleExportCsv}
      />
      <InscritosTable fase={faseAtual} rows={filteredSorted} onCancelRequest={(row) => setCancelTarget(row)} />
      {cancelTarget && (
        <CancelarInscricaoModal
          open
          mocktestName={mocktestName}
          mocktestStart={data.mocktestStart}
          fase={faseAtual}
          student={{
            name: cancelTarget.userName,
            email: cancelTarget.userEmail,
            registryDate: cancelTarget.registryDate,
            derivedStatus: cancelTarget.derivedStatus,
          }}
          onConfirm={handleConfirmCancel}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
}

export default InscritosPanel;
